import React from 'react';
import { TokenInfo } from '../../types';

interface TokenInfoCardProps {
  token: TokenInfo;
  showDetails?: boolean;
  compact?: boolean;
  onClick?: () => void;
}

export const TokenInfoCard: React.FC<TokenInfoCardProps> = ({ 
  token, 
  showDetails = true, 
  compact = false,
  onClick 
}) => {
  const formatPrice = (price?: number) => {
    if (!price) return 'N/A';
    
    // Pour les très petites valeurs (moins de 0.000001)
    if (price < 0.000001) {
      return `$${price.toFixed(8)}`;
    }
    // Pour les petites valeurs (moins de 0.01)
    if (price < 0.01) {
      return `$${price.toFixed(6)}`;
    }
    // Pour les valeurs moyennes (moins de 1)
    if (price < 1) {
      return `$${price.toFixed(4)}`;
    }
    // Pour les valeurs normales
    if (price < 1000) {
      return `$${price.toFixed(2)}`;
    }
    // Pour les grandes valeurs
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMarketCap = (marketCap?: number) => {
    if (!marketCap) return 'N/A';
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    if (marketCap >= 1e3) return `$${(marketCap / 1e3).toFixed(2)}K`;
    return `$${marketCap.toFixed(2)}`;
  };

  const formatVolume = (volume?: number) => {
    if (!volume) return 'N/A';
    if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
    if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`;
    return `$${volume.toFixed(2)}`;
  };

  if (compact) {
    return (
      <div 
        onClick={onClick}
        className={`flex items-center space-x-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors ${
          onClick ? 'cursor-pointer' : ''
        }`}
      >
        {token.logoURI && (
          <img src={token.logoURI} alt={token.symbol} className="w-8 h-8 rounded-full" />
        )}
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-white">{token.symbol}</span>
            {token.verified && <span className="text-green-500 text-sm">✓</span>}
          </div>
          <p className="text-sm text-gray-400">{formatPrice(token.price)}</p>
        </div>
        {token.priceChange24h !== undefined && (
          <span className={`text-sm font-medium ${
            token.priceChange24h > 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {token.priceChange24h > 0 ? '+' : ''}{token.priceChange24h.toFixed(2)}%
          </span>
        )}
      </div>
    );
  }

  return (
    <div 
      onClick={onClick}
      className={`bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-blue-500 transition-all duration-300 ${
        onClick ? 'cursor-pointer hover:scale-105' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {token.logoURI && (
            <img src={token.logoURI} alt={token.symbol} className="w-12 h-12 rounded-full" />
          )}
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-bold text-white">{token.symbol}</h3>
              {token.verified && (
                <span className="text-green-500">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
            <p className="text-gray-400">{token.name}</p>
          </div>
        </div>
        
        {/* Social Links */}
        {showDetails && token.socialLinks && (
          <div className="flex space-x-2">
            {token.socialLinks.twitter && (
              <a 
                href={token.socialLinks.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            )}
            {token.socialLinks.telegram && (
              <a 
                href={token.socialLinks.telegram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9.78 12.78l.79-.8 4.178-4.177c.39-.39.39-1.02 0-1.41s-1.02-.39-1.41 0l-4.976 4.976c-.39.39-.39 1.02 0 1.41l4.976 4.976c.39.39 1.02.39 1.41 0s.39-1.02 0-1.41L9.78 12.78z"/>
                </svg>
              </a>
            )}
            {token.socialLinks.website && (
              <a 
                href={token.socialLinks.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline space-x-3">
          <span className="text-3xl font-bold text-white">{formatPrice(token.price)}</span>
          {token.priceChange24h !== undefined && (
            <span className={`text-lg font-semibold ${
              token.priceChange24h > 0 ? 'text-green-500' : 'text-red-500'
            }`}>
              {token.priceChange24h > 0 ? '+' : ''}{token.priceChange24h.toFixed(2)}%
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      {showDetails && (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Market Cap</p>
            <p className="font-semibold text-white">{formatMarketCap(token.marketCap)}</p>
          </div>
          <div>
            <p className="text-gray-400">Volume 24h</p>
            <p className="font-semibold text-white">{formatVolume(token.volume24h)}</p>
          </div>
          {token.circulatingSupply && (
            <div>
              <p className="text-gray-400">Supply</p>
              <p className="font-semibold text-white">
                {token.circulatingSupply >= 1e9 
                  ? `${(token.circulatingSupply / 1e9).toFixed(2)}B`
                  : token.circulatingSupply >= 1e6 
                    ? `${(token.circulatingSupply / 1e6).toFixed(2)}M`
                    : token.circulatingSupply.toLocaleString()
                }
              </p>
            </div>
          )}
          <div>
            <p className="text-gray-400">Mint Address</p>
            <p className="font-mono text-xs text-white truncate">
              {token.address.slice(0, 8)}...{token.address.slice(-8)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
